// src/pages/OBOverview.js
// Uppdaterad: Visar detaljerad OB-information per pass (samma som TimeReportPopup)
// Använder getAllObDetailsForShift från TimeReportPopup för att hämta OB-detaljer

import React, { useCallback, useEffect, useMemo, useState } from "react";
import LoadingIndicator from "../components/LoadingIndicator";
import { FaSearch, FaCloudMoon, FaRegClock, FaMoneyBillWave, FaUser, FaChevronDown, FaChevronUp } from "react-icons/fa";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  doc,
  getDoc,
  getDocs,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";

const obOverviewCss = `
.ob-person-toggle { display: flex; align-items: center; gap: 8px; width: 100%; border: 0; padding: 0; background: transparent; color: inherit; font: inherit; text-align: left; cursor: pointer; }
.ob-person-toggle:focus-visible, .ob-calculation summary:focus-visible { outline: 2px solid #6c31ed; outline-offset: 5px; border-radius: 4px; }
.ob-person-heading { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px; font-size: 15px; color: #1f2937; }
.ob-person-heading > span, .ob-muted, .ob-help, .ob-no-compensation { color: #5f6b7a; }
.ob-help { margin: 8px 0 16px; font-size: 13px; line-height: 1.6; }
.ob-shift-list { display: grid; gap: 12px; }
.ob-shift-card { background: white; border: 1px solid #dfe5ee; border-radius: 10px; overflow: hidden; }
.ob-shift-summary { display: grid; grid-template-columns: minmax(150px, 1.4fr) 1fr 1fr 1fr; align-items: center; gap: 16px; padding: 16px; font-size: 14px; color: #1f2937; }
.ob-muted { display: block; font-size: 12px; margin-bottom: 3px; }
.ob-shift-amount { text-align: right; }
.ob-shift-amount strong { color: #047857; font-size: 17px; }
.ob-calculation { border-top: 1px solid #edf0f5; }
.ob-calculation summary { padding: 10px 16px; cursor: pointer; color: #4f35a5; font-weight: 600; }
.ob-hide, .ob-calculation[open] .ob-show { display: none; }
.ob-calculation[open] .ob-hide { display: inline; }
.ob-calculation-scroll { overflow-x: auto; padding: 0 16px; }
.ob-calculation-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.ob-calculation-table caption { text-align: left; padding: 8px 0; font-weight: 600; }
.ob-calculation-table th, .ob-calculation-table td { padding: 10px 12px; text-align: left; border-bottom: 1px solid #edf0f5; }
.ob-calculation-table thead { background: #f4f6fa; color: #526075; }
.ob-calculation-table td { white-space: nowrap; font-variant-numeric: tabular-nums; }
.ob-calculation-table td:last-child, .ob-calculation-table thead th:last-child { text-align: right; }
.ob-calculation-table tfoot { font-weight: 700; color: #1f2937; }
.ob-calculation > .ob-help { padding: 0 16px; }
.ob-no-compensation { margin: 0; padding: 10px 16px; border-top: 1px solid #edf0f5; font-size: 13px; }
@media (max-width: 700px) {
  .ob-shift-summary { grid-template-columns: 1fr 1fr; gap: 12px; }
  .ob-shift-amount { text-align: left; }
}
`;

// ==================== HJÄLPFUNKTIONER (kopierade från TimeReportPopup) ====================

function safeParseDate(dateValue) {
  if (!dateValue) return null;

  try {
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }

    if (typeof dateValue === "object" && typeof dateValue.toDate === "function") {
      const parsed = dateValue.toDate();
      return parsed instanceof Date && !isNaN(parsed.getTime()) ? parsed : null;
    }

    if (typeof dateValue === "string" || typeof dateValue === "number") {
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
  } catch (error) {
    console.error("Fel vid datumtolkning:", error);
    return null;
  }
}

function getMonthIndexFromSwedish(monthName) {
  const months = {
    Januari: 0,
    Februari: 1,
    Mars: 2,
    April: 3,
    Maj: 4,
    Juni: 5,
    Juli: 6,
    Augusti: 7,
    September: 8,
    Oktober: 9,
    November: 10,
    December: 11,
  };

  return months[monthName] ?? 0;
}

function pad(n) {
  return String(n).padStart(2, "0");
}

function formatDateKey(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function normalizeDayName(day) {
  const map = {
    monday: "måndag",
    tuesday: "tisdag",
    wednesday: "onsdag",
    thursday: "torsdag",
    friday: "fredag",
    saturday: "lördag",
    sunday: "söndag",
    måndag: "måndag",
    tisdag: "tisdag",
    onsdag: "onsdag",
    torsdag: "torsdag",
    fredag: "fredag",
    lördag: "lördag",
    söndag: "söndag",
    Måndag: "måndag",
    Tisdag: "tisdag",
    Onsdag: "onsdag",
    Torsdag: "torsdag",
    Fredag: "fredag",
    Lördag: "lördag",
    Söndag: "söndag",
  };

  return map[String(day || "").trim()] || String(day || "").toLowerCase().trim();
}

function getSwedishWeekday(date) {
  const names = ["söndag", "måndag", "tisdag", "onsdag", "torsdag", "fredag", "lördag"];
  return names[date.getDay()];
}

function timeToMinutes(timeString) {
  if (!timeString || typeof timeString !== "string") return 0;

  const [h, m] = timeString.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return h * 60 + m;
}

function formatTimeFromMinutes(minutes) {
  const normalizedMinutes = Math.max(0, Math.min(1440, Number(minutes || 0)));
  const hours = Math.floor(normalizedMinutes / 60);
  const mins = normalizedMinutes % 60;
  return `${pad(hours)}:${pad(mins)}`;
}

function calculateTotalShiftMinutes(shift) {
  if (!shift?.startTime || !shift?.endTime) return 0;

  const startMinutes = timeToMinutes(String(shift.startTime));
  const endMinutes = timeToMinutes(String(shift.endTime));

  let totalMinutes = endMinutes - startMinutes;
  if (totalMinutes < 0) {
    totalMinutes += 24 * 60;
  }

  return totalMinutes;
}

function calculateTotalBreakMinutes(employeeData) {
  let totalBreakMinutes = 0;

  if (employeeData?.breaks && Array.isArray(employeeData.breaks)) {
    employeeData.breaks.forEach((breakItem) => {
      if (breakItem.appliesToWholeShift) return;
      
      if (breakItem.startTime && breakItem.endTime) {
        const start = timeToMinutes(breakItem.startTime);
        const end = timeToMinutes(breakItem.endTime);
        
        let breakMinutes = end - start;
        if (breakMinutes < 0) breakMinutes += 24 * 60;
        totalBreakMinutes += breakMinutes;
      }
    });
  }

  return totalBreakMinutes;
}

function calculateShiftHours(shift, employeeData = null) {
  if (!shift?.startTime || !shift?.endTime) return 0;

  try {
    const totalMinutes = calculateTotalShiftMinutes(shift);
    let breakMinutes = 0;
    
    if (employeeData) {
      breakMinutes = calculateTotalBreakMinutes(employeeData);
      
      if (employeeData.breaks && Array.isArray(employeeData.breaks)) {
        employeeData.breaks.forEach((breakItem) => {
          if (breakItem.appliesToWholeShift) {
            const totalHours = totalMinutes / 60;
            if (totalHours >= 5 && totalHours < 8) {
              breakMinutes = Math.max(breakMinutes, 30);
            } else if (totalHours >= 8) {
              breakMinutes = Math.max(breakMinutes, 60);
            }
          }
        });
      }
    } else {
      breakMinutes = Number(shift.breakMinutes || shift.break || 0);
    }
    
    let workedMinutes = Math.max(0, totalMinutes - breakMinutes);
    
    if (employeeData?.absences && Array.isArray(employeeData.absences)) {
      employeeData.absences.forEach((absenceItem) => {
        let absenceMinutes = 0;
        
        if (absenceItem.appliesToWholeShift) {
          absenceMinutes = workedMinutes;
        } else if (absenceItem.startTime && absenceItem.endTime) {
          const start = timeToMinutes(absenceItem.startTime);
          const end = timeToMinutes(absenceItem.endTime);
          
          let minutes = end - start;
          if (minutes < 0) minutes += 24 * 60;
          absenceMinutes = minutes;
        }
        
        workedMinutes = Math.max(0, workedMinutes - absenceMinutes);
      });
    }
    
    return workedMinutes / 60;
  } catch (error) {
    console.error("Fel vid beräkning av passets timmar:", error);
    return 0;
  }
}

function getShiftSegments(shift) {
  const shiftDate = safeParseDate(shift?.date);
  if (!shiftDate || !shift?.startTime || !shift?.endTime) return [];

  const startMinutes = timeToMinutes(String(shift.startTime));
  const endMinutes = timeToMinutes(String(shift.endTime));

  if (startMinutes === endMinutes) return [];

  if (endMinutes > startMinutes) {
    return [{
      date: new Date(shiftDate),
      dateKey: formatDateKey(shiftDate),
      startMinute: startMinutes,
      endMinute: endMinutes,
    }];
  }

  const nextDate = addDays(shiftDate, 1);

  return [
    {
      date: new Date(shiftDate),
      dateKey: formatDateKey(shiftDate),
      startMinute: startMinutes,
      endMinute: 1440,
    },
    {
      date: new Date(nextDate),
      dateKey: formatDateKey(nextDate),
      startMinute: 0,
      endMinute: endMinutes,
    },
  ];
}

function getDateKey(value) {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);

  const parsed = safeParseDate(value);
  return parsed ? formatDateKey(parsed) : "";
}

function sourceAppliesToMinute({
  dateKey,
  previousDateKey,
  minute,
  start,
  end,
  sourceDateKey,
}) {
  if (start <= end) {
    return sourceDateKey === dateKey && minute >= start && minute < end;
  }

  return (
    (sourceDateKey === dateKey && minute >= start && minute < 1440) ||
    (sourceDateKey === previousDateKey && minute >= 0 && minute < end)
  );
}

function getRuleDays(rule) {
  const rawDays =
    rule?.days ||
    rule?.selectedDays ||
    rule?.weekdays ||
    rule?.selectedWeekdays ||
    [];

  return Array.isArray(rawDays) ? rawDays.map(normalizeDayName) : [];
}

function getActiveObSourcesAtMinute(minute, date, obRules, holidays, customObEntries = []) {
  const sources = [];

  const dateKey = formatDateKey(date);
  const previousDate = addDays(date, -1);
  const previousDateKey = formatDateKey(previousDate);

  const currentWeekday = getSwedishWeekday(date);
  const previousWeekday = getSwedishWeekday(previousDate);

  (obRules || []).forEach((rule) => {
    const ruleName = rule?.name || rule?.title || "OB";

    if (rule?.useSpecificDates) {
      const specificDates = Array.isArray(rule?.specificDates)
        ? rule.specificDates
        : [];

      specificDates.forEach((item, index) => {
        const amount = Number(item?.amount ?? rule?.amount ?? 0);
        if (amount <= 0) return;

        const sourceDateKey = getDateKey(item?.date);
        const start = timeToMinutes(
          item?.startTime || rule?.startTime || "00:00"
        );
        const end = timeToMinutes(
          item?.endTime || rule?.endTime || "23:59"
        );

        if (
          sourceAppliesToMinute({
            dateKey,
            previousDateKey,
            minute,
            start,
            end,
            sourceDateKey,
          })
        ) {
          sources.push({
            name: item?.name || ruleName,
            amount,
            type: "obRule",
            sourceId: `${rule?.id || ruleName}__specific__${index}`,
          });
        }
      });

      return;
    }

    const amount = Number(
      rule?.amount ??
      rule?.obAmount ??
      rule?.value ??
      0
    );

    if (amount <= 0) return;

    const start = timeToMinutes(rule?.startTime || rule?.from || "00:00");
    const end = timeToMinutes(rule?.endTime || rule?.to || "23:59");
    const days = getRuleDays(rule);

    const appliesToday = days.length === 0 || days.includes(currentWeekday);
    const appliesPreviousDay = days.length === 0 || days.includes(previousWeekday);

    if (start <= end) {
      if (appliesToday && minute >= start && minute < end) {
        sources.push({
          name: ruleName,
          amount,
          type: "obRule",
          sourceId: rule?.id || ruleName,
        });
      }
    } else {
      if (appliesToday && minute >= start && minute < 1440) {
        sources.push({
          name: ruleName,
          amount,
          type: "obRule",
          sourceId: rule?.id || ruleName,
        });
      }

      if (appliesPreviousDay && minute >= 0 && minute < end) {
        sources.push({
          name: ruleName,
          amount,
          type: "obRule",
          sourceId: rule?.id || ruleName,
        });
      }
    }
  });

  (holidays || []).forEach((holiday) => {
    const amount = Number(holiday?.amount || 0);
    if (amount <= 0) return;

    const holidayDateKey = getDateKey(holiday?.date);
    const start = timeToMinutes(holiday?.startTime || "00:00");
    const end = timeToMinutes(holiday?.endTime || "23:59");

    if (
      sourceAppliesToMinute({
        dateKey,
        previousDateKey,
        minute,
        start,
        end,
        sourceDateKey: holidayDateKey,
      })
    ) {
      sources.push({
        name: holiday?.name || "Helgdag",
        amount,
        type: "holiday",
        sourceId: holiday?.id || holidayDateKey,
      });
    }
  });

  (customObEntries || []).forEach((customOb) => {
    const amount = Number(customOb?.amount || 0);
    if (amount <= 0) return;

    const name = customOb?.name || customOb?.description || "Eget OB-tillägg";
    const customDateKey = getDateKey(customOb?.date);
    const start = timeToMinutes(customOb?.startTime || "00:00");
    const end = timeToMinutes(customOb?.endTime || "23:59");

    if (
      sourceAppliesToMinute({
        dateKey,
        previousDateKey,
        minute,
        start,
        end,
        sourceDateKey: customDateKey,
      })
    ) {
      sources.push({
        name,
        amount,
        type: "custom",
        customObId: customOb.id,
        sourceId: customOb?.id || `${customDateKey}__${name}`,
      });
    }
  });

  return sources;
}

function buildAbsenceIntervals(segments, employeeData) {
  const absenceIntervals = [];

  if (employeeData?.absences && Array.isArray(employeeData.absences)) {
    employeeData.absences.forEach((absenceItem) => {
      if (absenceItem.appliesToWholeShift) {
        segments.forEach((segment) => {
          absenceIntervals.push({
            start: segment.startMinute,
            end: segment.endMinute,
          });
        });
      } else if (absenceItem.startTime && absenceItem.endTime) {
        const start = timeToMinutes(absenceItem.startTime);
        const end = timeToMinutes(absenceItem.endTime);

        if (end < start) {
          segments.forEach((segment) => {
            if (segment.startMinute <= start && segment.endMinute >= 1440) {
              absenceIntervals.push({ start, end: 1440 });
            }
            if (segment.startMinute <= end && segment.endMinute >= 0) {
              absenceIntervals.push({ start: 0, end });
            }
          });
        } else {
          segments.forEach((segment) => {
            const overlapStart = Math.max(segment.startMinute, start);
            const overlapEnd = Math.min(segment.endMinute, end);
            if (overlapEnd > overlapStart) {
              absenceIntervals.push({
                start: overlapStart,
                end: overlapEnd,
              });
            }
          });
        }
      }
    });
  }

  return absenceIntervals;
}

// SAMMA SOM TimeReportPopup - räknar varje OB-källa separat
function calculateObForShift(shift, obRules, holidays, obEnabled = true, employeeData = null, customObEntries = []) {
  if (!obEnabled) {
    return { hours: 0, value: 0 };
  }

  const segments = getShiftSegments(shift);
  if (segments.length === 0) {
    return { hours: 0, value: 0 };
  }

  const absenceIntervals = buildAbsenceIntervals(segments, employeeData);

  const isMinuteAbsent = (minute) => {
    return absenceIntervals.some(
      (interval) => minute >= interval.start && minute < interval.end
    );
  };

  let obMinutes = 0;
  let obValue = 0;

  segments.forEach((segment) => {
    for (let minute = segment.startMinute; minute < segment.endMinute; minute++) {
      if (isMinuteAbsent(minute)) continue;

      const activeSources = getActiveObSourcesAtMinute(
        minute,
        segment.date,
        obRules,
        holidays,
        customObEntries
      ).filter((source) => Number(source.amount || 0) > 0);

      activeSources.forEach((source) => {
        obMinutes += 1;
        obValue += Number(source.amount || 0) / 60;
      });
    }
  });

  return {
    hours: obMinutes / 60,
    value: obValue,
  };
}

// ============================================================
// FUNKTIONER FÖR OB-DETALJER (kopierade från TimeReportPopup)
// ============================================================

function mergeConsecutiveObDetails(obDetails) {
  if (!obDetails || obDetails.length === 0) return [];
  
  const merged = [];
  let current = { ...obDetails[0] };
  
  for (let i = 1; i < obDetails.length; i++) {
    const next = obDetails[i];
    
    const isSameType = current.type === next.type && 
                       current.rate === next.rate &&
                       current.sourceType === next.sourceType;
    
    const isConsecutive = current.endTime === next.startTime;
    
    if (isSameType && isConsecutive) {
      current.endTime = next.endTime;
      current.durationMinutes += next.durationMinutes;
      current.durationHours = current.durationMinutes / 60;
      current.value = (current.rate / 60) * current.durationMinutes;
    } else {
      merged.push(current);
      current = { ...next };
    }
  }
  merged.push(current);
  
  return merged;
}

function getAllObDetailsForShift(shift, obRules, holidays, employeeData = null, customObEntries = []) {
  if (!shift?.startTime || !shift?.endTime) {
    return [];
  }

  const segments = getShiftSegments(shift);
  if (segments.length === 0) {
    return [];
  }

  const absenceIntervals = buildAbsenceIntervals(segments, employeeData);

  const isMinuteAbsent = (minute) => {
    return absenceIntervals.some(
      (interval) => minute >= interval.start && minute < interval.end
    );
  };

  const allObDetails = [];

  segments.forEach((segment) => {
    const openIntervals = new Map();

    const makeKey = (source) => {
      return `${source.type || "ob"}__${source.name || "OB"}__${source.amount}__${source.sourceId || source.customObId || ""}`;
    };

    const closeInterval = (key, endMinute) => {
      const interval = openIntervals.get(key);
      if (!interval) return;

      const durationMinutes = endMinute - interval.startMinute;
      if (durationMinutes > 0) {
        allObDetails.push({
          type: interval.name,
          sourceType: interval.sourceType,
          rate: interval.amount,
          startTime: formatTimeFromMinutes(interval.startMinute),
          endTime: formatTimeFromMinutes(endMinute),
          durationMinutes,
          durationHours: durationMinutes / 60,
          value: (interval.amount / 60) * durationMinutes,
          customObId: interval.customObId,
        });
      }
      openIntervals.delete(key);
    };

    for (let minute = segment.startMinute; minute < segment.endMinute; minute++) {
      if (isMinuteAbsent(minute)) {
        Array.from(openIntervals.keys()).forEach((key) => {
          closeInterval(key, minute);
        });
        continue;
      }

      const activeSources = getActiveObSourcesAtMinute(minute, segment.date, obRules, holidays, customObEntries)
        .filter((source) => Number(source.amount || 0) > 0);

      const activeKeys = new Set(activeSources.map(makeKey));

      Array.from(openIntervals.keys()).forEach((key) => {
        if (!activeKeys.has(key)) {
          closeInterval(key, minute);
        }
      });

      activeSources.forEach((source) => {
        const key = makeKey(source);
        if (!openIntervals.has(key)) {
          openIntervals.set(key, {
            name: source.name,
            sourceType: source.type,
            amount: source.amount,
            startMinute: minute,
            customObId: source.customObId,
          });
        }
      });
    }

    Array.from(openIntervals.keys()).forEach((key) => {
      closeInterval(key, segment.endMinute);
    });
  });

  allObDetails.sort((a, b) => {
    const aStart = timeToMinutes(a.startTime);
    const bStart = timeToMinutes(b.startTime);
    if (aStart !== bStart) return aStart - bStart;
    
    if (a.sourceType === "holiday" && b.sourceType !== "holiday") return -1;
    if (a.sourceType !== "holiday" && b.sourceType === "holiday") return 1;
    
    return String(a.type).localeCompare(String(b.type), "sv");
  });

  return mergeConsecutiveObDetails(allObDetails);
}

// Formatera timmar
const formatHours = (hours) => {
  return Number(hours || 0).toLocaleString("sv-SE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Formatera valuta (med ören)
const formatCurrency = (amount) => {
  return Number(amount || 0).toLocaleString("sv-SE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + " kr";
};

// Formatera datum för visning
function formatDateDisplay(date) {
  if (!date) return "N/A";
  const d = safeParseDate(date);
  if (!d) return "N/A";
  return d.toLocaleDateString("sv-SE");
}

// Formatera tid för visning
function formatTimeDisplay(timeString) {
  if (!timeString) return "N/A";
  return timeString.substring(0, 5);
}

// ==================== HUVUDKOMPONENT ====================

export default function OBOverview() {
  const { currentUser } = useAuth();
  const { activeCompany } = useCompany();

  const [companyRootUid, setCompanyRootUid] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [obRules, setObRules] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customObEntries, setCustomObEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readySources, setReadySources] = useState(() => new Set());
  const markSourceReady = useCallback((source) => {
    setReadySources((current) => new Set(current).add(source));
  }, []);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [expandedEmployee, setExpandedEmployee] = useState(null);

  useEffect(() => {
    const currentDate = new Date();
    const months = ["Januari", "Februari", "Mars", "April", "Maj", "Juni", "Juli", "Augusti", "September", "Oktober", "November", "December"];
    setSelectedMonth(months[currentDate.getMonth()]);
    setSelectedYear(currentDate.getFullYear().toString());
  }, []);

  const monthsInSwedish = [
    "Januari", "Februari", "Mars", "April", "Maj", "Juni",
    "Juli", "Augusti", "September", "Oktober", "November", "December"
  ];

  const generateYears = () => {
    const currentYearNum = new Date().getFullYear();
    const years = [];
    for (let year = 2020; year <= currentYearNum + 2; year++) {
      years.push(year.toString());
    }
    return years;
  };

  const years = generateYears();

  // Hämta companyRootUid
  useEffect(() => {
    let isMounted = true;

    const resolveCompanyRootUid = async () => {
      if (activeCompany?.companyAccountUid) {
        setCompanyRootUid(activeCompany.companyAccountUid);
        setLoading(false);
        return;
      }
      if (!currentUser?.uid) {
        if (isMounted) {
          setCompanyRootUid(null);
          setLoading(false);
        }
        return;
      }

      setLoading(true);

      try {
        const currentUserRef = doc(db, "users", currentUser.uid);
        const currentUserSnap = await getDoc(currentUserRef);

        if (!currentUserSnap.exists()) {
          if (isMounted) {
            setCompanyRootUid(currentUser.uid);
            setLoading(false);
          }
          return;
        }

        const currentUserData = currentUserSnap.data();
        const userCompanyId = currentUserData.companyId || null;

        if (!userCompanyId) {
          if (isMounted) {
            setCompanyRootUid(currentUser.uid);
            setLoading(false);
          }
          return;
        }

        const superAdminQuery = query(
          collection(db, "users"),
          where("companyId", "==", userCompanyId),
          where("role", "==", "super-admin"),
          limit(1)
        );

        const superAdminSnap = await getDocs(superAdminQuery);

        if (!superAdminSnap.empty) {
          if (isMounted) {
            setCompanyRootUid(superAdminSnap.docs[0].id);
            setLoading(false);
          }
          return;
        }

        const adminQuery = query(
          collection(db, "users"),
          where("companyId", "==", userCompanyId),
          where("role", "==", "admin"),
          limit(1)
        );

        const adminSnap = await getDocs(adminQuery);

        if (!adminSnap.empty) {
          if (isMounted) {
            setCompanyRootUid(adminSnap.docs[0].id);
            setLoading(false);
          }
          return;
        }

        if (isMounted) {
          setCompanyRootUid(currentUser.uid);
          setLoading(false);
        }
      } catch (error) {
        console.error("Fel vid hämtning av företagets root uid:", error);
        if (isMounted) {
          setCompanyRootUid(currentUser.uid);
          setLoading(false);
        }
      }
    };

    resolveCompanyRootUid();

    return () => {
      isMounted = false;
    };
  }, [currentUser?.uid, activeCompany?.companyAccountUid]);

  // Hämta customObEntries
  useEffect(() => {
    if (!companyRootUid) {
      setCustomObEntries([]);
      return;
    }

    const customObRef = collection(db, "users", companyRootUid, "customObEntries");

    const unsubscribe = onSnapshot(
      customObRef,
      (snapshot) => {
        const entries = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setCustomObEntries(entries);
        markSourceReady("customObEntries");
      },
      (error) => {
        console.error("Fel vid hämtning av egna OB-tillägg:", error);
        setCustomObEntries([]);
        markSourceReady("customObEntries");
      }
    );

    return () => unsubscribe();
  }, [companyRootUid, markSourceReady]);

  // Hämta anställda
  useEffect(() => {
    if (!companyRootUid) {
      setEmployees([]);
      return;
    }

    const employedRef = collection(db, "users", companyRootUid, "employed");

    const unsubscribe = onSnapshot(
      employedRef,
      (snapshot) => {
        const users = [];

        snapshot.forEach((userDoc) => {
          const userData = userDoc.data();
          const payroll = userData.payroll || {};

          let fullName = userData.name || "";
          if (userData.firstName && userData.lastName) {
            fullName = `${userData.firstName} ${userData.lastName}`;
          } else if (userData.firstName) {
            fullName = userData.firstName;
          } else if (userData.lastName) {
            fullName = userData.lastName;
          } else if (userData.displayName) {
            fullName = userData.displayName;
          } else if (userData.userName) {
            fullName = userData.userName;
          }

          users.push({
            id: userDoc.id,
            name: fullName || "Användare",
            email: userData.email || "",
            role: userData.role || "user",
            obEnabled: payroll.obEnabled ?? true,
            payroll,
          });
        });

        users.sort((a, b) => (a.name || "").localeCompare(b.name || "", "sv-SE"));

        setEmployees(users);
        markSourceReady("employees");
      },
      (error) => {
        console.error("Fel vid hämtning av anställda:", error);
        setEmployees([]);
        markSourceReady("employees");
      }
    );

    return () => unsubscribe();
  }, [companyRootUid, markSourceReady]);

  // Hämta pass
  useEffect(() => {
    if (!companyRootUid) {
      setShifts([]);
      return;
    }

    const shiftsRef = collection(db, "users", companyRootUid, "shifts");

    const unsubscribe = onSnapshot(
      shiftsRef,
      (snapshot) => {
        const shiftList = [];
        snapshot.forEach((shiftDoc) => {
          shiftList.push({
            id: shiftDoc.id,
            ...shiftDoc.data(),
          });
        });
        setShifts(shiftList);
        markSourceReady("shifts");
      },
      (error) => {
        console.error("Fel vid hämtning av pass:", error);
        setShifts([]);
        markSourceReady("shifts");
      }
    );

    return () => unsubscribe();
  }, [companyRootUid, markSourceReady]);

  // Hämta OB-regler
  useEffect(() => {
    if (!companyRootUid) {
      setObRules([]);
      return;
    }

    const obRulesRef = collection(db, "users", companyRootUid, "obRules");
    const q = query(obRulesRef, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const rules = [];
        snapshot.forEach((docItem) => {
          rules.push({
            id: docItem.id,
            ...docItem.data(),
          });
        });
        setObRules(rules);
        markSourceReady("obRules");
      },
      (error) => {
        console.error("Fel vid hämtning av OB-regler:", error);
        setObRules([]);
        markSourceReady("obRules");
      }
    );

    return () => unsubscribe();
  }, [companyRootUid, markSourceReady]);

  // Hämta helgdagar
  useEffect(() => {
    if (!companyRootUid) {
      setHolidays([]);
      return;
    }

    const holidaysRef = collection(db, "users", companyRootUid, "holidays");
    const q = query(holidaysRef, orderBy("date", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = [];
        snapshot.forEach((docItem) => {
          list.push({
            id: docItem.id,
            ...docItem.data(),
          });
        });
        setHolidays(list);
        markSourceReady("holidays");
      },
      (error) => {
        console.error("Fel vid hämtning av helgdagar:", error);
        setHolidays([]);
        markSourceReady("holidays");
      }
    );

    return () => unsubscribe();
  }, [companyRootUid, markSourceReady]);

  const employeeMap = useMemo(() => {
    const map = {};
    employees.forEach((employee) => {
      map[employee.id] = employee;
    });
    return map;
  }, [employees]);

  // Beräkna OB för varje pass - SAMMA LOGIK SOM TimeReportPopup
  const employeesWithShifts = useMemo(() => {
    if (!selectedMonth || !selectedYear) return [];

    const selectedMonthIndex = getMonthIndexFromSwedish(selectedMonth);
    const selectedYearNumber = Number(selectedYear);

    const filteredShifts = shifts.filter((shift) => {
      const shiftDate = safeParseDate(shift.date);
      if (!shiftDate) return false;

      const sameMonth = shiftDate.getMonth() === selectedMonthIndex;
      const sameYear = shiftDate.getFullYear() === selectedYearNumber;
      return sameMonth && sameYear;
    });

    const shiftsWithOB = filteredShifts.map((shift) => {
      let employeeId = null;
      let employeeData = {
        breaks: shift.breaks || [],
        overtime: shift.overtime || [],
        absences: shift.absences || []
      };

      if (shift.employees && Array.isArray(shift.employees) && shift.employees.length > 0) {
        employeeId = shift.employees[0].employeeId;
        employeeData = shift.employees[0];
      } else if (shift.employeeId) {
        employeeId = shift.employeeId;
      }

      const employee = employeeMap[employeeId];
      const obEnabled = employee?.obEnabled ?? true;
      
      const employeeCustomObEntries = customObEntries.filter(
        entry => entry.employeeId === employeeId
      );

      // Använd samma OB-beräkning som TimeReportPopup
      const obResult = calculateObForShift(
        shift,
        obRules,
        holidays,
        obEnabled,
        employeeData,
        employeeCustomObEntries
      );

      // Hämta OB-detaljer (samma som TimeReportPopup)
      const obDetails = getAllObDetailsForShift(
        shift,
        obRules,
        holidays,
        employeeData,
        employeeCustomObEntries
      );

      const workedHours = calculateShiftHours(shift, employeeData);
      const employeeName = employeeId && employeeMap[employeeId] ? employeeMap[employeeId].name : "Okänd anställd";

      return {
        ...shift,
        employeeId: employeeId,
        employeeName: employeeName,
        obHours: obResult.hours,
        obValue: obResult.value,
        obDetails: obDetails,
        workedHours: workedHours,
        dateFormatted: formatDateDisplay(shift.date),
        startTimeFormatted: formatTimeDisplay(shift.startTime),
        endTimeFormatted: formatTimeDisplay(shift.endTime),
      };
    });

    const employeeGroupMap = new Map();
    
    shiftsWithOB.forEach((shift) => {
      const employeeId = shift.employeeId || "unknown";
      const employeeName = shift.employeeName;
      
      if (!employeeGroupMap.has(employeeId)) {
        employeeGroupMap.set(employeeId, {
          id: employeeId,
          name: employeeName,
          shifts: [],
          totalOBHours: 0,
          totalOBValue: 0,
        });
      }
      
      const employeeData = employeeGroupMap.get(employeeId);
      employeeData.shifts.push(shift);
      employeeData.totalOBHours += shift.obHours || 0;
      employeeData.totalOBValue += shift.obValue || 0;
    });
    
    const employeeList = Array.from(employeeGroupMap.values());
    employeeList.sort((a, b) => a.name.localeCompare(b.name, "sv-SE"));
    
    return employeeList;
  }, [shifts, selectedMonth, selectedYear, obRules, holidays, employeeMap, customObEntries]);

  const filteredEmployees = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();

    return employeesWithShifts.filter((employee) => {
      if (!q) return true;
      return employee.name.toLowerCase().includes(q);
    });
  }, [employeesWithShifts, searchQuery]);

  const summary = useMemo(() => {
    const totalOBHours = filteredEmployees.reduce(
      (sum, employee) => sum + (employee.totalOBHours || 0), 0
    );
    const totalOBValue = filteredEmployees.reduce(
      (sum, employee) => sum + (employee.totalOBValue || 0), 0
    );

    return { totalOBHours, totalOBValue };
  }, [filteredEmployees]);

  // Växla expandering av anställd
  const toggleExpand = (employeeId) => {
    setExpandedEmployee(expandedEmployee === employeeId ? null : employeeId);
  };

  const styles = {
    page: {
      minHeight: "100vh",
      background: "#f6f7fb",
      padding: "22px 24px 40px",
      fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      color: "#1f2937",
      boxSizing: "border-box",
    },
    container: {
      width: "100%",
      maxWidth: "1400px",
      margin: "0 auto",
    },
    topRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: "16px",
      flexWrap: "wrap",
      marginBottom: "14px",
    },
    title: {
      margin: 0,
      fontSize: "42px",
      lineHeight: 1.1,
      fontWeight: 800,
      color: "#101b3a",
      letterSpacing: "-1px",
      display: "flex",
      alignItems: "center",
      gap: "16px",
    },
    titleIcon: {
      background: "linear-gradient(135deg, #6c31ed, #8f58ff)",
      padding: "10px",
      borderRadius: "18px",
      color: "#fff",
      display: "inline-flex",
    },
    subtitle: {
      margin: "12px 0 0",
      fontSize: "15px",
      lineHeight: 1.45,
      color: "#5f6b85",
      maxWidth: "640px",
    },
    divider: {
      height: 1,
      background: "#dde3ef",
      margin: "16px 0 18px",
      width: "100%",
    },
    summaryPanel: {
      background: "linear-gradient(135deg, #f0f4ff, #e8edfc)",
      borderRadius: "14px",
      padding: "20px",
      marginBottom: "24px",
      border: "1px solid #e0e8fb",
    },
    summaryTitle: {
      margin: 0,
      fontSize: "20px",
      fontWeight: 700,
      color: "#2552d8",
    },
    summaryMeta: {
      margin: "6px 0 16px",
      fontSize: "14px",
      fontWeight: 600,
      color: "#2552d8",
    },
    summaryGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: "16px",
    },
    summaryCard: {
      background: "#fff",
      border: "1px solid #d8deea",
      borderRadius: "14px",
      padding: "18px 16px",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    summaryCardLabel: {
      fontSize: "13px",
      fontWeight: 600,
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    summaryCardValue: {
      fontSize: "28px",
      fontWeight: 800,
      color: "#111827",
    },
    filtersWrap: {
      display: "grid",
      gridTemplateColumns: "1fr 148px 148px",
      gap: "10px",
      background: "#fff",
      borderRadius: "12px",
      padding: "12px",
      marginBottom: "20px",
      border: "1px solid #e5e9f0",
    },
    searchWrap: {
      position: "relative",
      width: "100%",
    },
    searchIcon: {
      position: "absolute",
      left: "14px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#9ca3af",
      fontSize: "14px",
    },
    searchInput: {
      width: "100%",
      height: "42px",
      borderRadius: "10px",
      border: "1px solid #e2e6ed",
      padding: "0 14px 0 40px",
      fontSize: "14px",
      background: "#fff",
      color: "#111827",
      outline: "none",
      boxSizing: "border-box",
    },
    select: {
      width: "100%",
      height: "42px",
      borderRadius: "10px",
      border: "1px solid #e2e6ed",
      padding: "0 12px",
      fontSize: "14px",
      background: "#fff",
      color: "#111827",
      outline: "none",
      boxSizing: "border-box",
      cursor: "pointer",
    },
    tableWrap: {
      background: "#fff",
      border: "1px solid #e5e9f0",
      borderRadius: "14px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: "500px",
    },
    thead: {
      background: "#f8fafd",
    },
    th: {
      textAlign: "left",
      fontSize: "13px",
      fontWeight: 700,
      color: "#4a5568",
      padding: "14px 16px",
      borderBottom: "1px solid #e5e9f0",
    },
    td: {
      fontSize: "14px",
      color: "#1a202c",
      padding: "14px 16px",
      borderBottom: "1px solid #edf2f7",
    },
    obHoursCell: {
      fontWeight: 600,
      color: "#3b82f6",
    },
    obValueCell: {
      fontWeight: 600,
      color: "#10b981",
    },
    emptyState: {
      background: "#fff",
      border: "1px solid #e5e9f0",
      borderRadius: "14px",
      padding: "48px",
      textAlign: "center",
      color: "#6b7280",
    },
    expandRow: {
      cursor: "pointer",
      transition: "background 0.2s",
    },
    detailsRow: {
      background: "#fafcff",
    },
    detailsCell: {
      padding: "20px 24px",
      borderBottom: "1px solid #edf2f7",
    },
    detailsContent: {
      fontSize: "13px",
      color: "#4a5568",
      lineHeight: 1.6,
    },
    shiftItem: {
      padding: "14px",
      background: "#f8fafc",
      borderRadius: "10px",
      marginBottom: "12px",
      border: "1px solid #eef2f6",
    },
    shiftItemLast: {
      marginBottom: 0,
    },
    shiftDate: {
      fontWeight: 700,
      color: "#1a202c",
      marginBottom: "8px",
      fontSize: "14px",
    },
    shiftDetails: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      gap: "12px",
      marginTop: "10px",
    },
    obRuleList: {
      marginTop: "10px",
      paddingTop: "8px",
      borderTop: "1px solid #e2e8f0",
    },
    obRuleItem: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "6px 0",
      borderBottom: "1px dashed #e2e8f0",
      fontSize: "12px",
    },
    obRuleName: {
      fontWeight: 600,
      color: "#3b82f6",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    obRuleValue: {
      fontWeight: 600,
      color: "#10b981",
    },
  };

  if (!selectedMonth || !selectedYear || loading || readySources.size < 5) {
    return <LoadingIndicator message="Laddar OB-data..." fullPage />;
  }

  return (
    <div style={styles.page}>
      <style>{obOverviewCss}</style>
      <div style={styles.container}>
        <div style={styles.topRow}>
          <div>
            <h1 style={styles.title}>
              <span style={styles.titleIcon}>
                <FaCloudMoon size={28} />
              </span>
              OB-översikt
            </h1>
            <p style={styles.subtitle}>
              Översikt över OB-ersättning (obekväm arbetstid) för vald period för <strong>{activeCompany?.companyName || "företaget"}</strong>.
              Klicka på en anställd för att se detaljerad information per pass.
            </p>
          </div>
        </div>

        <div style={styles.divider} />

        <div style={styles.summaryPanel}>
          <h2 style={styles.summaryTitle}>SAMMANFATTNING</h2>
          <div style={styles.summaryMeta}>
            {selectedMonth.toUpperCase()} {selectedYear}
          </div>

          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryCardLabel}>
                <FaRegClock style={{ marginRight: "6px" }} />
                OB-timmar
              </div>
              <div style={styles.summaryCardValue}>
                {formatHours(summary.totalOBHours)} h
              </div>
            </div>

            <div style={styles.summaryCard}>
              <div style={styles.summaryCardLabel}>
                <FaMoneyBillWave style={{ marginRight: "6px" }} />
                OB-ersättning
              </div>
              <div style={styles.summaryCardValue}>
                {formatCurrency(summary.totalOBValue)}
              </div>
            </div>
          </div>
        </div>

        <div style={styles.filtersWrap}>
          <div style={styles.searchWrap}>
            <FaSearch style={styles.searchIcon} />
            <input
              type="text"
              placeholder="Sök efter anställd..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
          </div>

          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            style={styles.select}
          >
            {monthsInSwedish.map((month) => (
              <option key={month} value={month}>{month}</option>
            ))}
          </select>

          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            style={styles.select}
          >
            {years.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <LoadingIndicator message="Laddar OB-data..." />
        ) : filteredEmployees.length === 0 ? (
          <div style={styles.emptyState}>
            <FaRegClock size={32} style={{ marginBottom: "12px", color: "#9ca3af" }} />
            <div>Inga anställda med OB-berättigade pass hittades för vald period.</div>
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead style={styles.thead}>
                  <tr>
                    <th style={styles.th}>Namn</th>
                    <th style={styles.th}>OB-timmar (totalt)</th>
                    <th style={styles.th}>OB-ersättning (totalt)</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredEmployees.map((employee) => (
                    <React.Fragment key={employee.id}>
                      <tr 
                        style={styles.expandRow}
                        onClick={() => toggleExpand(employee.id)}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#f8f9fc"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <td style={styles.td}>
                          <button type="button" className="ob-person-toggle" aria-expanded={expandedEmployee === employee.id} onClick={(event) => { event.stopPropagation(); toggleExpand(employee.id); }}>
                            <FaUser size={14} color="#6b7280" />
                            {employee.name}
                            <span style={{ marginLeft: "auto", fontSize: "11px", color: "#9ca3af" }}>
                              {expandedEmployee === employee.id ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                            </span>
                          </button>
                        </td>
                        <td style={{ ...styles.td, ...styles.obHoursCell }}>
                          {employee.totalOBHours > 0 ? formatHours(employee.totalOBHours) : "-"}
                        </td>
                        <td style={{ ...styles.td, ...styles.obValueCell }}>
                          {employee.totalOBValue > 0 ? formatCurrency(employee.totalOBValue) : "-"}
                        </td>
                      </tr>
                      
                      {expandedEmployee === employee.id && (
                        <tr style={styles.detailsRow}>
                          <td colSpan="3" style={styles.detailsCell}>
                            <div style={styles.detailsContent}>
                              <div className="ob-person-heading">
                                <strong>Pass för {employee.name}</strong>
                                <span>{selectedMonth} {selectedYear} · {employee.shifts.length} pass</span>
                              </div>
                              <p className="ob-help">Öppna ”Visa OB-beräkning” för att se vilka tider och tillägg som ingår. Om flera tillägg gäller samtidigt räknas tiden för varje tillägg.</p>
                              <div className="ob-shift-list">
                                {[...employee.shifts].sort((a, b) =>
                                  (safeParseDate(a.date)?.getTime() || 0) - (safeParseDate(b.date)?.getTime() || 0) ||
                                  String(a.startTime).localeCompare(String(b.startTime))
                                ).map((shift) => (
                                  <article key={shift.id} className="ob-shift-card">
                                    <div className="ob-shift-summary">
                                      <div>
                                        <strong>{shift.dateFormatted}</strong>
                                        <span className="ob-muted">Pass {shift.startTimeFormatted}–{shift.endTimeFormatted}</span>
                                      </div>
                                      <div>
                                        <span className="ob-muted">Arbetad tid</span>
                                        <span>{formatHours(shift.workedHours)} h</span>
                                      </div>
                                      <div>
                                        <span className="ob-muted">OB-timmar</span>
                                        <span>{formatHours(shift.obHours)} h</span>
                                      </div>
                                      <div className="ob-shift-amount">
                                        <span className="ob-muted">OB-ersättning</span>
                                        <strong>{formatCurrency(shift.obValue)}</strong>
                                      </div>
                                    </div>
                                    {shift.obValue > 0 && shift.obDetails?.length > 0 ? (
                                      <details className="ob-calculation">
                                        <summary><span className="ob-show">Visa OB-beräkning</span><span className="ob-hide">Dölj OB-beräkning</span></summary>
                                        <div className="ob-calculation-scroll">
                                          <table className="ob-calculation-table">
                                            <caption>OB-tillägg för passet {shift.dateFormatted}</caption>
                                            <thead><tr><th scope="col">Tillägg</th><th scope="col">Tid med OB</th><th scope="col">Antal timmar</th><th scope="col">Kr per timme</th><th scope="col">Ersättning</th></tr></thead>
                                            <tbody>
                                              {shift.obDetails.map((detail, idx) => (
                                                <tr key={idx}>
                                                  <th scope="row">{detail.type}</th>
                                                  <td>{detail.startTime}–{detail.endTime}</td>
                                                  <td>{formatHours(detail.durationHours)} h</td>
                                                  <td>{formatCurrency(detail.rate)}</td>
                                                  <td>{formatCurrency(detail.value)}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                            <tfoot><tr><th scope="row" colSpan="4">Summa OB för passet</th><td>{formatCurrency(shift.obValue)}</td></tr></tfoot>
                                          </table>
                                        </div>
                                        <p className="ob-help">Ersättning = antal OB-timmar × kr per timme.</p>
                                      </details>
                                    ) : <p className="ob-no-compensation">Ingen OB-ersättning för detta pass.</p>}
                                  </article>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
